import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { createApiUrlWithPrefix } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  Plus,
  Search,
  CheckCircle,
  XCircle,
  User,
  CreditCard,
  Receipt,
  Eye,
  Trash2,
  Shield,
  Calendar,
  BookOpen,
  Gift,
  RefreshCw,
  CircleDollarSign
} from 'lucide-react';

function RiderPayments() {
  const { t, i18n } = useTranslation();
  const { user: currentUser } = useAuth();
  const { getSettingAsNumber } = useSettings();
  const isSuperAdmin = currentUser?.role === 4 || currentUser?.role === 'SuperAdmin';
  const insurancePrice = getSettingAsNumber('insurance.annual.price', 10000);
  const forfaitPrice = getSettingAsNumber('forfait.monthly.price', 5000);
  const extraLessonPrice = getSettingAsNumber('extra.lesson.price', 650);
  const demiForfaitPrice = getSettingAsNumber('forfait.demi.price', 3000);
  const demiForfaitLessonsCount = getSettingAsNumber('forfait.demi.lessons.count', 4);
  const payPerSessionPrice = getSettingAsNumber('pay.per.session.price', 700);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHistoricalPaymentModal, setShowHistoricalPaymentModal] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [riderPayments, setRiderPayments] = useState([]);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState('all');
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [isUsingDemoData, setIsUsingDemoData] = useState(false);
  const [riderForfaits, setRiderForfaits] = useState([]); // Store existing forfaits
  const [riderInsurance, setRiderInsurance] = useState(null); // Store rider insurance status
  const [riderActiveForfait, setRiderActiveForfait] = useState(null); // Store rider active forfait (current month)
  const [lastPaidForfait, setLastPaidForfait] = useState(null); // Store the last paid forfait month
  const [isLoadingRiderStatus, setIsLoadingRiderStatus] = useState(false); // Loading state for rider status
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Bonus lessons modal state
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusRider, setBonusRider] = useState(null);
  const [bonusLessonCount, setBonusLessonCount] = useState(1);
  const [givingBonus, setGivingBonus] = useState(false);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    type: 1,
    description: '',
    extraLessonCount: 1,
    selectedMonths: [],
    forfaitCategory: 'Full',
    activateOnCredit: false
  });

  // Historical payment form state (separate from normal payment)
  const [historicalPaymentForm, setHistoricalPaymentForm] = useState({
    amount: String(insurancePrice),
    type: 1,
    description: 'Annual Insurance',
    extraLessonCount: 1,
    selectedMonths: [],
    paymentDate: new Date().toISOString().split('T')[0] // Allow custom date for historical payments
  });

    const [refreshKey, setRefreshKey] = useState(0);
const paymentTypes = useMemo(() => {
    // Payment type requirements:
    // 1. Annual Insurance: Always available
    // 2. Full Forfait (8 lessons): Requires valid insurance
    // 3. Extra Lessons: Requires valid insurance
    // 4. Demi Forfait (4 lessons): Requires valid insurance
    // 5. Pay Per Session (1 lesson): Requires valid insurance
    
    const insuranceExpiry = riderInsurance?.expiryDate || riderInsurance?.ExpiryDate;
    const insuranceCancelled = riderInsurance?.isCancelled || riderInsurance?.IsCancelled;
    const insuranceExists = riderInsurance && !insuranceCancelled && insuranceExpiry;
    const insuranceExpired = insuranceExists && new Date(insuranceExpiry) <= new Date();
    const hasValidInsurance = insuranceExists && !insuranceExpired;
    
    const currentDate = new Date();
    const forfaitYear = riderActiveForfait?.year || riderActiveForfait?.Year;
    const forfaitMonth = riderActiveForfait?.month || riderActiveForfait?.Month;
    const forfaitCancelled = riderActiveForfait?.isCancelled || riderActiveForfait?.IsCancelled;
    const hasActiveForfait = riderActiveForfait && 
      !forfaitCancelled &&
      forfaitYear === currentDate.getFullYear() &&
      forfaitMonth === currentDate.getMonth() + 1;
    
    return [
      { 
        value: 1, 
        label: t('common.annualInsurance'), 
        icon: Shield, 
        color: 'bg-blue-100 text-blue-800',
        description: insuranceExpired ? 'Insurance expired - renewal required' : t('common.annualInsuranceDescription'),
        defaultAmount: insurancePrice,
        enabled: true,
        status: hasValidInsurance ? 'paid' : insuranceExpired ? 'expired' : 'not_paid',
        expiryDate: riderInsurance?.expiryDate || riderInsurance?.ExpiryDate || null
      },
      { 
        value: 2, 
        label: 'Full Forfait (8 lessons)', 
        icon: Calendar, 
        color: 'bg-green-100 text-green-800',
        description: `${forfaitPrice.toLocaleString()} DA/month - 8 lessons`,
        defaultAmount: forfaitPrice,
        enabled: hasValidInsurance,
        disabledReason: !hasValidInsurance ? (insuranceExpired ? 'Renew insurance first (expired)' : t('common.requiresValidInsurance')) : null,
        status: hasActiveForfait ? 'paid' : 'not_paid'
      },
      {
        value: 4,
        label: 'Demi Forfait (4 lessons)',
        icon: Calendar,
        color: 'bg-teal-100 text-teal-800',
        description: `${demiForfaitPrice.toLocaleString()} DA/month - 4 lessons`,
        defaultAmount: demiForfaitPrice,
        enabled: hasValidInsurance,
        disabledReason: !hasValidInsurance ? (insuranceExpired ? 'Renew insurance first (expired)' : t('common.requiresValidInsurance')) : null,
        status: hasActiveForfait ? 'paid' : 'not_paid'
      },
      {
        value: 5,
        label: 'Pay Per Session (1 lesson)',
        icon: BookOpen,
        color: 'bg-purple-100 text-purple-800',
        description: `${payPerSessionPrice.toLocaleString()} DA - 1 session`,
        defaultAmount: payPerSessionPrice,
        enabled: hasValidInsurance,
        disabledReason: !hasValidInsurance ? (insuranceExpired ? 'Renew insurance first (expired)' : t('common.requiresValidInsurance')) : null,
        status: 'available'
      },
      {
        value: 3, 
        label: t('common.extraLessons'), 
        icon: BookOpen, 
        color: 'bg-orange-100 text-orange-800',
        description: `${extraLessonPrice.toLocaleString()} DA/lesson - add to any month`,
        defaultAmount: extraLessonPrice,
        enabled: hasValidInsurance,
        disabledReason: !hasValidInsurance ? (insuranceExpired ? 'Renew insurance first (expired)' : t('common.requiresValidInsurance')) : null,
        status: hasValidInsurance ? 'available' : 'not_available'
      }
    ];
  }, [t, riderInsurance, riderActiveForfait, insurancePrice, forfaitPrice, extraLessonPrice, demiForfaitPrice, payPerSessionPrice]);

  // Set initial payment form values
  useEffect(() => {
    const initialPaymentType = paymentTypes.find(type => type.value === paymentForm.type);
    if (initialPaymentType?.defaultAmount !== undefined) {
      setPaymentForm(prev => ({
        ...prev,
        amount: initialPaymentType.defaultAmount.toString(),
        description: initialPaymentType.label
      }));
    }
  }, [paymentForm.type, paymentTypes]);

  const fetchRiders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        role: '0',
        _t: Date.now(),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(createApiUrlWithPrefix(`users?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const riders = response.data.users || response.data || [];
      const riderIds = riders.map(r => r.id || r.Id);
      
      // Fetch payment status for all riders in a single bulk call
      console.log(`📦 Fetching bulk statuses for ${riderIds.length} riders...`);
      const bulkStatusResponse = await axios.post(
        createApiUrlWithPrefix('RiderPayment/bulk-status'),
        riderIds,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      ).catch(err => {
        console.error('❌ Error fetching bulk statuses:', err);
        return { data: {} };
      });
      
      const statusMap = bulkStatusResponse.data || {};

      // Fetch debt balances for all riders in a single bulk call
      let debtBalanceMap = {};
      try {
        const debtResponse = await axios.post(
          createApiUrlWithPrefix('RiderPayment/debt-balances'),
          riderIds,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        debtBalanceMap = debtResponse.data || {};
      } catch (err) {
        console.error('Error fetching debt balances:', err);
      }

      const ridersWithStatus = riders.map((rider) => {
        const id = rider.id || rider.Id;
        const statusData = statusMap[id] || {};
        const debtBalance = debtBalanceMap[id] || 0;
        
        const insurance = statusData.insurance || statusData.Insurance;
        const activeForfait = statusData.activeForfait || statusData.ActiveForfait;
        
        // Determine insurance status
        const insuranceExpiry = insurance?.expiryDate || insurance?.ExpiryDate;
        const insuranceCancelled = insurance?.isCancelled || insurance?.IsCancelled;
        const isInsured = insurance && 
          !insuranceCancelled && 
          insuranceExpiry && 
          new Date(insuranceExpiry) > new Date();
        
        // Determine forfait status
        const forfaitYear = activeForfait?.year || activeForfait?.Year;
        const forfaitMonth = activeForfait?.month || activeForfait?.Month;
        const forfaitCancelled = activeForfait?.isCancelled || activeForfait?.IsCancelled;
        const currentDate = new Date();
        const hasActiveForfait = activeForfait && 
          !forfaitCancelled &&
          forfaitYear === currentDate.getFullYear() &&
          forfaitMonth === currentDate.getMonth() + 1;
        
        // Extra lessons - now directly available from status response
        const extraLessons = statusData.extraLessonsCount || statusData.ExtraLessonsCount || 0;
        const extraAmount = statusData.extraLessonsAmount || statusData.ExtraLessonsAmount || 0;
        const forfaitBonusLessons = activeForfait?.freeBonusLessonCount ?? activeForfait?.FreeBonusLessonCount ?? 0;
        const userBonusLessons = statusData.freeBonusLessons ?? statusData.FreeBonusLessons ?? 0;
        const bonusLessons = userBonusLessons + forfaitBonusLessons;
        
        const totalPaidThisYear = statusData.totalPaidThisYear ?? statusData.TotalPaidThisYear ?? 0;

        return {
          ...rider,
          totalPaidThisYear,
          debtBalance,
          insuranceStatus: {
            IsInsured: isInsured,
            ExpiryDate: insuranceExpiry || null
          },
          forfaitStatus: {
            HasActiveForfait: hasActiveForfait,
            Forfait: hasActiveForfait ? {
              ...activeForfait,
              Year: forfaitYear,
              Month: forfaitMonth,
              year: forfaitYear,
              month: forfaitMonth,
              TotalLessons: activeForfait.totalLessons || activeForfait.TotalLessons,
              totalLessons: activeForfait.totalLessons || activeForfait.TotalLessons,
              UsedLessons: activeForfait.usedLessons || activeForfait.UsedLessons || 0,
              usedLessons: activeForfait.usedLessons || activeForfait.UsedLessons || 0
            } : null
          },
          extraLessonsSummary: {
            Count: extraLessons,
            TotalAmount: extraAmount
          },
          bonusLessonsSummary: {
            Count: bonusLessons,
            TotalAmount: 0
          }
        };
      });
      
      setRiders(ridersWithStatus);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || response.data.length);
    } catch (error) {
      console.error('Error fetching riders:', error);
      toast.error(t('common.failedToFetchRiders'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, t]);

  const fetchRiderPayments = useCallback(async (riderId, paymentType = 'all') => {
    try {
      let payments = [];
      
      if (riderId) {
        // Fetch payments for specific rider using new RiderPayment endpoint
        const response = await axios.get(createApiUrlWithPrefix(`RiderPayment/rider/${riderId}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        payments = response.data || [];
        
        // Filter by payment type if not 'all'
        if (paymentType !== 'all') {
          payments = payments.filter(payment => payment.type === paymentType);
        }
      } else {
        // Fetch all payments for admin view
        const response = await axios.get(createApiUrlWithPrefix('RiderPayment/all'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        payments = response.data || [];
      }
      
      // Sort by payment date (newest first)
      // Sort payments: non-historical first, then historical, both sorted by date (newest first)
      payments.sort((a, b) => {
        // First, separate historical from non-historical
        if (a.isHistorical !== b.isHistorical) {
          return a.isHistorical ? 1 : -1; // Non-historical first (false comes before true)
        }
        // If both are same type, sort by date (newest first)
        return new Date(b.paymentDate) - new Date(a.paymentDate);
      });
      setRiderPayments(payments);
      setIsUsingDemoData(false);
    } catch (error) {
      console.error('Error fetching rider payments from API:', error);
      
      // Show error message and set empty array
      if (error.code === 'ERR_NETWORK' || error.message.includes('Connection refused')) {
        toast.error(t('common.apiServerNotRunning'));
      } else if (error.response?.status === 404) {
        toast.error('Payment endpoints not found. The API server may need to be restarted.');
      } else {
        toast.error('Failed to fetch payment data from API.');
      }
      
      setRiderPayments([]);
      setIsUsingDemoData(false);
    }
  }, []);

  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  const handlePaymentTypeChange = (type) => {
    const typeValue = parseInt(type);
    const paymentType = paymentTypes.find(pt => pt.value === typeValue);
    console.log('Payment type changed:', { type, typeValue, paymentType });
    
    // Check if the selected type is enabled
    if (!paymentType?.enabled) {
      toast.error(t('common.thisPaymentTypeNotAvailable'));
      return;
    }
    
    setPaymentForm(prev => {
      let amount = paymentType?.defaultAmount || 0;
      let selectedMonths = prev.selectedMonths;
      let forfaitCategory = prev.forfaitCategory;
      
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      
      // For Full Forfait or Demi Forfait, check if current month has forfait
      if ((typeValue === 2 || typeValue === 4) && selectedMonths.length === 0) {
        const currentMonthHasForfait = riderForfaits.some(f => {
          const fYear = f.year || f.Year;
          const fMonth = f.month || f.Month;
          const fCancelled = f.isCancelled || f.IsCancelled;
          return fYear === currentYear && 
                 fMonth === currentMonth && 
                 !fCancelled;
        });
        
        if (!currentMonthHasForfait) {
          selectedMonths = [{
            year: currentYear,
            month: currentMonth
          }];
        } else {
          selectedMonths = [];
          amount = 0;
        }
      }
      
      // Pay Per Session - no month selection, use current month
      if (typeValue === 5) {
        selectedMonths = [];
      }
      
      // Set forfait category
      forfaitCategory = typeValue === 2 ? 'Full' 
        : typeValue === 4 ? 'Demi' 
        : typeValue === 5 ? 'PayPerSession' 
        : prev.forfaitCategory;
      
      // Only multiply for Extra Lessons
      if (typeValue === 3) {
        amount = amount * prev.extraLessonCount;
      }
      
      return {
        ...prev,
        type: typeValue,
        amount: amount.toString(),
        description: paymentType?.label || '',
        selectedMonths: selectedMonths,
        forfaitCategory: forfaitCategory
      };
    });
  };


  const handleExtraLessonCountChange = (extraLessonCount) => {
    const count = parseInt(extraLessonCount);
    setPaymentForm(prev => {
      const paymentType = paymentTypes.find(pt => pt.value === prev.type);
      let amount = paymentType?.defaultAmount || 0;
      
      // Only multiply for Extra Lessons
      if (prev.type === 3) {
        amount = amount * count;
      }
      
      return {
        ...prev,
        extraLessonCount: count,
        amount: amount.toString()
      };
    });
  };

  const toggleMonthSelection = (year, month) => {
    setPaymentForm(prev => {
      const isSelected = prev.selectedMonths.some(m => m.year === year && m.month === month);
      
      let newSelectedMonths;
      if (isSelected) {
        // Remove month
        newSelectedMonths = prev.selectedMonths.filter(m => !(m.year === year && m.month === month));
      } else {
        // Add month
        newSelectedMonths = [...prev.selectedMonths, { year, month }];
      }
      
      // Update amount based on selected months
      const newAmount = newSelectedMonths.length * forfaitPrice;
      
      return {
        ...prev,
        selectedMonths: newSelectedMonths,
        amount: newAmount.toString()
      };
    });
  };

  const isMonthSelected = (year, month) => {
    return paymentForm.selectedMonths.some(m => m.year === year && m.month === month);
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    
    if (!selectedRider) {
      toast.error(t('common.pleaseSelectRider'));
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to create payments');
      return;
    }

    // Validate form data
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!paymentForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    const type = parseInt(paymentForm.type);
    const isForfaitType = type === 2 || type === 4 || type === 5;
    if (paymentForm.activateOnCredit && !isForfaitType) {
      toast.error('On credit activation is only available for forfait payments');
      return;
    }
    if (paymentForm.activateOnCredit && isForfaitType && paymentForm.selectedMonths.length === 0) {
      toast.error('Please select at least one month for on-credit activation');
      return;
    }

    const amount = parseFloat(paymentForm.amount);
    const extraLessonCount = parseInt(paymentForm.extraLessonCount);

    try {
      // Get current user ID with better error handling
      const currentUserId = localStorage.getItem('userId');
      if (!currentUserId) {
        toast.error('User session expired. Please login again.');
        return;
      }

      const paymentType = parseInt(paymentForm.type);
      const forfaitCategory = paymentType === 2 ? 'Full' 
        : paymentType === 4 ? 'Demi' 
        : paymentType === 5 ? 'PayPerSession' 
        : null;

      const paymentData = {
        riderId: selectedRider.id,
        memberRole: 'rider',
        amount: parseFloat(paymentForm.amount),
        type: paymentType,
        description: paymentForm.description.trim(),
        processedBy: parseInt(currentUserId),
        extraLessonCount: parseInt(paymentForm.extraLessonCount),
        forfaitMonths: (paymentType === 2 || paymentType === 4) && paymentForm.selectedMonths.length > 0 
          ? paymentForm.selectedMonths 
          : paymentType === 5 ? [{ year: new Date().getFullYear(), month: new Date().getMonth() + 1 }] 
          : null,
        isHistorical: false,
        forfaitCategory: forfaitCategory,
        activateOnCredit: !!paymentForm.activateOnCredit
      };

      console.log('Creating payment with data:', paymentData);
      console.log('Selected months:', paymentForm.selectedMonths);
      console.log('Amount calculation:', `${paymentForm.selectedMonths.length} months × ${forfaitPrice} = ${paymentForm.selectedMonths.length * forfaitPrice}`);
      console.log('Token from localStorage:', localStorage.getItem('token'));

      // Create payment using RiderPayment controller
      const response = await axios.post(createApiUrlWithPrefix('RiderPayment'), paymentData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Payment created successfully:', response.data);
      toast.success(`${paymentTypes.find(pt => pt.value === paymentForm.type)?.label} ${paymentForm.activateOnCredit ? 'activated on credit' : 'payment created successfully'}`);
      
      // Clear selected months immediately (before closing modal)
      const paidMonths = paymentForm.selectedMonths;
      setPaymentForm(prev => ({
        ...prev,
        selectedMonths: []
      }));
      
      // If it was a forfait payment, refresh the rider's forfait status
      if ((paymentForm.type === 2 || paymentForm.type === 4 || paymentForm.type === 5) && selectedRider) {
        // Refresh forfaits first
        await fetchRiderForfaits(selectedRider.id || selectedRider.Id);
        // Also refresh the full payment status to update insurance and active forfait
        try {
          const statusResponse = await axios.get(
            createApiUrlWithPrefix(`RiderPayment/rider/${selectedRider.id || selectedRider.Id}/status`),
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          const statusData = statusResponse.data || {};
          setRiderInsurance(statusData.insurance || statusData.Insurance || null);
          setRiderActiveForfait(statusData.activeForfait || statusData.ActiveForfait || null);
          setLastPaidForfait(statusData.lastPaidForfait || statusData.LastPaidForfait || null);
        } catch (error) {
          console.error('Error refreshing rider status:', error);
        }
      }
      
      // Close modal and reset form
      setShowPaymentModal(false);
      setPaymentForm({
        amount: '',
        type: 1,
        description: '',
        extraLessonCount: 1,
        selectedMonths: [],
        forfaitCategory: 'Full',
        activateOnCredit: false
      });
      
      // Refresh riders to update their status in the list
      await fetchRiders();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.message || 'Failed to create payment');
    }
  };

  // Handler for historical payments (separate from normal payments)
  const handleCreateHistoricalPayment = async (e) => {
    e.preventDefault();
    
    if (!selectedRider) {
      toast.error(t('common.pleaseSelectRider'));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to create payments');
      return;
    }

    if (!historicalPaymentForm.amount || parseFloat(historicalPaymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Description is optional for historical payments

    try {
      const currentUserId = localStorage.getItem('userId');
      if (!currentUserId) {
        toast.error('User session expired. Please login again.');
        return;
      }

      const paymentData = {
        riderId: selectedRider.id,
        amount: parseFloat(historicalPaymentForm.amount),
        type: parseInt(historicalPaymentForm.type),
        description: historicalPaymentForm.description.trim(),
        processedBy: parseInt(currentUserId),
        extraLessonCount: parseInt(historicalPaymentForm.extraLessonCount),
        forfaitMonths: historicalPaymentForm.selectedMonths.length > 0 ? historicalPaymentForm.selectedMonths : null,
        isHistorical: true, // Mark as historical
        paymentDate: historicalPaymentForm.paymentDate // Use custom date
      };

      await axios.post(createApiUrlWithPrefix('RiderPayment'), paymentData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Historical payment added successfully');
      
      // Close modal and reset form
      setShowHistoricalPaymentModal(false);
      setHistoricalPaymentForm({
        amount: String(insurancePrice),
        type: 1,
        description: 'Annual Insurance',
        extraLessonCount: 1,
        selectedMonths: [],
        paymentDate: new Date().toISOString().split('T')[0]
      });
      
      // Refresh payment history if open
      if (showPaymentHistory && selectedRider) {
        await fetchRiderPayments(selectedRider.id, selectedPaymentType);
      }
      
      // Refresh riders to update their status
      await fetchRiders();
      
    } catch (apiError) {
      console.error('API error:', apiError);
      console.error('Error response:', apiError.response?.data);
      console.error('Error status:', apiError.response?.status);
      
      // Show specific error messages
      if (apiError.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else if (apiError.response?.status === 400) {
        const errorMessage = apiError.response?.data?.message || apiError.response?.data || 'Invalid payment data';
        console.error('Validation error:', errorMessage);
        toast.error(errorMessage);
      } else if (apiError.response?.status === 500) {
        const errorMessage = apiError.response?.data?.message || 'Server error. Please try again.';
        toast.error(errorMessage);
      } else {
        toast.error('Failed to create payment. Please check your connection and try again.');
      }
    }
  };

  const handleGiveBonusLessons = async (e) => {
    e.preventDefault();
    if (!bonusRider) return;

    if (bonusLessonCount <= 0) {
      toast.error('Lesson count must be greater than 0');
      return;
    }

    const riderId = bonusRider.id || bonusRider.Id;
    if (!riderId) {
      toast.error('Invalid rider');
      return;
    }

    const currentUserId = localStorage.getItem('userId');
    if (!currentUserId) {
      toast.error('User session expired. Please login again.');
      return;
    }

    setGivingBonus(true);
    try {
      await axios.post(
        createApiUrlWithPrefix(`RiderPayment/rider/${riderId}/bonus-lessons`),
        { lessonCount: bonusLessonCount, processedBy: parseInt(currentUserId) },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(`Successfully gave ${bonusLessonCount} bonus lesson${bonusLessonCount > 1 ? 's' : ''}`);
      setShowBonusModal(false);
      setBonusRider(null);
      setBonusLessonCount(1);
      await fetchRiders();
    } catch (error) {
      console.error('Error giving bonus lessons:', error);
      toast.error(error.response?.data?.message || 'Failed to give bonus lessons');
    } finally {
      setGivingBonus(false);
    }
  };

  const handleCancelPayment = async (paymentId, paymentType) => {
    try {
      // Cancel payment using new RiderPayment controller
      await axios.post(createApiUrlWithPrefix(`RiderPayment/${paymentId}/cancel`), {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Payment cancelled successfully');
      
      // Refresh data
      await fetchRiders();
      if (showPaymentHistory && selectedRider) {
        await fetchRiderPayments(selectedRider.id, selectedPaymentType);
      }
    } catch (apiError) {
      console.error('API error:', apiError);
      
      // Show error message
      if (apiError.response?.status === 404) {
        toast.error('Payment endpoints not available. Please check API configuration.');
      } else if (apiError.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else {
        toast.error('Failed to cancel payment. Please try again.');
      }
    }
  };

  const fetchRiderForfaits = async (riderId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`RiderPayment/rider/${riderId}/forfaits`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const forfaits = response.data || [];
      setRiderForfaits(forfaits);
      
      // Find the last paid forfait (most recent month that's not cancelled and not in the past)
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      
      // Get all valid forfaits (not cancelled, and not in the past) - handle both camelCase and PascalCase
      const validForfaits = forfaits.filter(f => {
        const cancelled = f.isCancelled || f.IsCancelled;
        if (cancelled) return false;
        const year = f.year || f.Year;
        const month = f.month || f.Month;
        const forfaitDate = new Date(year, month - 1);
        const currentDate = new Date(currentYear, currentMonth - 1);
        return forfaitDate >= currentDate;
      });
      
      // Sort by year and month (descending) and get the most recent
      if (validForfaits.length > 0) {
        const sorted = validForfaits.sort((a, b) => {
          const aYear = a.year || a.Year;
          const bYear = b.year || b.Year;
          const aMonth = a.month || a.Month;
          const bMonth = b.month || b.Month;
          if (aYear !== bYear) return bYear - aYear;
          return bMonth - aMonth;
        });
        setLastPaidForfait(sorted[0]);
      } else {
        setLastPaidForfait(null);
      }
      
      return forfaits;
    } catch (error) {
      console.error('Error fetching rider forfaits:', error);
      setRiderForfaits([]);
      setLastPaidForfait(null);
      return [];
    }
  };

  const fetchRiderInsurance = async (riderId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`RiderPayment/rider/${riderId}/insurance`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const insuranceData = response.data || null;
      console.log('✅ fetchRiderInsurance - Setting state with:', insuranceData);
      setRiderInsurance(insuranceData);
      return insuranceData;
    } catch (error) {
      console.error('❌ Error fetching rider insurance:', error);
      setRiderInsurance(null);
      return null;
    }
  };

  const fetchRiderActiveForfait = async (riderId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`RiderPayment/rider/${riderId}/forfait`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const forfaitData = response.data || null;
      console.log('✅ fetchRiderActiveForfait - Setting state with:', forfaitData);
      setRiderActiveForfait(forfaitData);
      return forfaitData;
    } catch (error) {
      console.error('❌ Error fetching rider active forfait:', error);
      setRiderActiveForfait(null);
      return null;
    }
  };

  const openPaymentModal = async (rider) => {
    setSelectedRider(rider);
    
    // Show modal immediately so user sees it
    setShowPaymentModal(true);
    
    // Initialize with data from rider object (immediate display)
    console.log('Opening payment modal with rider data:', { 
      rider: { id: rider.id, name: `${rider.firstName} ${rider.lastName}` },
      insuranceStatus: rider.insuranceStatus,
      forfaitStatus: rider.forfaitStatus
    });
    
    // Don't initialize with rider data - wait for backend status endpoint
    // This prevents stale/incorrect data from being shown
      setRiderInsurance(null);
    
    // Don't initialize with rider data - wait for backend status endpoint
    // This prevents stale/incorrect data from being shown
        setRiderActiveForfait(null);
    setRiderForfaits([]);
    setLastPaidForfait(null);
    
    // Determine initial payment type based on rider data
    const currentDate = new Date();
    const hasInsuranceFromRider = rider.insuranceStatus?.IsInsured && 
      rider.insuranceStatus.ExpiryDate &&
      new Date(rider.insuranceStatus.ExpiryDate) > new Date();
    
    const defaultType = hasInsuranceFromRider ? 2 : 1;
    // Don't pre-select current month here - wait until we fetch forfaits to check if it already has one
    const defaultSelectedMonths = [];
    
    const defaultAmount = defaultType === 1 ? insurancePrice : 0;
    const defaultDescription = defaultType === 1 
      ? t('common.annualInsurance') 
      : defaultType === 2 
      ? 'Full Forfait (8 lessons)'
      : defaultType === 4
      ? 'Demi Forfait (4 lessons)'
      : defaultType === 5
      ? 'Pay Per Session'
      : t('common.extraLessons');

    setPaymentForm({
      amount: defaultAmount.toString(),
      type: defaultType,
      description: defaultDescription,
      extraLessonCount: 1,
      selectedMonths: defaultSelectedMonths,
      forfaitCategory: defaultType === 2 ? 'Full' : defaultType === 4 ? 'Demi' : defaultType === 5 ? 'PayPerSession' : 'Full'
    });
    
    // Fetch all recap data from a single endpoint
    setIsLoadingRiderStatus(true);
    
    try {
      // Use single status endpoint that returns all needed data
      const response = await axios.get(createApiUrlWithPrefix(`RiderPayment/rider/${rider.id}/status`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const statusData = response.data || {};
      console.log('✅ Fetched rider payment status from backend:', statusData);
      
      // Update all states from the single response
      // Backend returns camelCase: insurance, activeForfait, allForfaits, lastPaidForfait
      const insuranceData = statusData.insurance || statusData.Insurance;
      const activeForfaitData = statusData.activeForfait || statusData.ActiveForfait;
      const allForfaitsData = statusData.allForfaits || statusData.AllForfaits || [];
      const lastPaidForfaitData = statusData.lastPaidForfait || statusData.LastPaidForfait;
      
      // Set states - ensure we preserve the object even if properties are nested
      setRiderInsurance(insuranceData || null);
      setRiderActiveForfait(activeForfaitData || null);
      setRiderForfaits(Array.isArray(allForfaitsData) ? allForfaitsData : []);
      setLastPaidForfait(lastPaidForfaitData || null);
      
      // Determine default payment type based on fresh backend data
      // Backend returns camelCase: isCancelled, expiryDate, year, month, etc.
      const insurance = statusData.insurance || statusData.Insurance;
      const hasValidInsurance = insurance && 
        !(insurance.isCancelled || insurance.IsCancelled) && 
        (insurance.expiryDate || insurance.ExpiryDate) &&
        new Date(insurance.expiryDate || insurance.ExpiryDate) > new Date();
      
      const activeForfait = statusData.activeForfait || statusData.ActiveForfait;
      const hasActiveForfait = activeForfait && 
        !(activeForfait.isCancelled || activeForfait.IsCancelled) &&
        (activeForfait.year || activeForfait.Year) === currentDate.getFullYear() &&
        (activeForfait.month || activeForfait.Month) === currentDate.getMonth() + 1;
      
      // Update default type if needed
      const updatedDefaultType = hasValidInsurance ? 2 : 1;
      
      // Check if current month already has a forfait before selecting it
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      const currentMonthHasForfait = Array.isArray(allForfaitsData) && allForfaitsData.some(f => {
        const fYear = f.year || f.Year;
        const fMonth = f.month || f.Month;
        const fCancelled = f.isCancelled || f.IsCancelled;
        return fYear === currentYear && 
               fMonth === currentMonth && 
               !fCancelled;
      });
      
      // Only select current month if it doesn't already have a forfait
      const updatedDefaultSelectedMonths = (updatedDefaultType === 2 && !currentMonthHasForfait) ? [{
        year: currentYear,
        month: currentMonth
      }] : [];
      
      const updatedDefaultAmount = updatedDefaultType === 1 ? insurancePrice : (updatedDefaultType === 2 && !currentMonthHasForfait) ? forfaitPrice : 0;
      const updatedDefaultDescription = updatedDefaultType === 1 
        ? t('common.annualInsurance') 
        : updatedDefaultType === 2 
        ? t('common.monthlyForfait') 
        : t('common.extraLessons');
      
      // Update form with correct defaults (check if current month has forfait)
      setPaymentForm(prev => {
        // Clear selected months if they already have forfaits
        const validSelectedMonths = prev.selectedMonths.filter(selectedMonth => {
          return !allForfaitsData.some(f => {
            const fYear = f.year || f.Year;
            const fMonth = f.month || f.Month;
            const fCancelled = f.isCancelled || f.IsCancelled;
            return fYear === selectedMonth.year && 
                   fMonth === selectedMonth.month && 
                   !fCancelled;
          });
        });
        
        return {
          ...prev,
          type: updatedDefaultType,
          amount: updatedDefaultAmount.toString(),
          description: updatedDefaultDescription,
          selectedMonths: validSelectedMonths.length > 0 ? validSelectedMonths : updatedDefaultSelectedMonths
        };
      });
      
      console.log('Updated payment modal with fresh backend data:', { 
        hasValidInsurance,
        insurance,
        hasActiveForfait,
        activeForfait,
        updatedDefaultType,
        lastPaidForfait: statusData.lastPaidForfait || statusData.LastPaidForfait
      });
      
    } catch (error) {
      console.error('❌ Error loading rider status from backend:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // If endpoint doesn't exist (404), set all to null/empty
      if (error.response?.status === 404) {
        setRiderInsurance(null);
        setRiderActiveForfait(null);
        setRiderForfaits([]);
        setLastPaidForfait(null);
        toast.error('Payment status endpoint not available. Backend may need to be updated.');
      } else {
        // For other errors, keep the initial data from rider object
      toast.error('Failed to refresh payment status from backend. Using cached data.');
      }
    } finally {
      setIsLoadingRiderStatus(false);
      console.log('Rider status loading complete. Final state:', {
        insurance: riderInsurance,
        activeForfait: riderActiveForfait,
        lastPaidForfait: lastPaidForfait,
        forfaitsCount: riderForfaits?.length || 0
      });
    }
  };

  const openPaymentHistory = async (rider) => {
    setSelectedRider(rider);
    setShowPaymentHistory(true);
    await fetchRiderPayments(rider.id, selectedPaymentType);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const paginatedRiders = riders;

  const getStatusIcon = (status) => {
    if (status) return <CheckCircle className="w-4 h-4 text-green-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const getStatusText = (status) => {
    return status ? t('common.active') : t('common.inactive');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  /* removed early loading return to prevent search bar unmounting 
if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  } 
*/

  return (
    <>
      <div className="bg-neutral-50 p-4 sm:p-6 lg:p-8" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} key={refreshKey}>
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header Section */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('riderPayments.title')}</h1>
                <p className="mt-2 text-sm text-gray-600">{t('riderPayments.subtitle')}</p>
              </div>
              {isUsingDemoData && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span className="text-sm text-yellow-800 font-medium">{t('common.demoMode')}</span>
                  </div>
                  <p className="text-xs text-yellow-600 mt-1">{t('common.usingSampleData')}</p>
                </div>
              )}
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
              <div className="flex flex-col lg:flex-row gap-4 items-center">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder={t('riderPayments.searchPlaceholder')}
                      value={searchTerm}
                      onChange={handleSearch}
                      className="w-full pl-10 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <select
                    value={currentYear}
                    onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  
                  <select
                    value={currentMonth}
                    onChange={(e) => setCurrentMonth(parseInt(e.target.value))}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>
                        {new Date(2024, month - 1).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    onClick={fetchRiders}
                    className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Riders Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedRiders.map((rider) => (
          <div key={rider.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                  </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {rider.firstName} {rider.lastName}
                  </h3>
                  <p className="text-sm text-gray-500">{rider.email}</p>
                </div>
              </div>
            </div>
            
            {/* Payment Status Cards - Simplified display */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Insurance */}
              <div className={`rounded-lg p-3 border-2 ${
                rider.insuranceStatus?.IsInsured && rider.insuranceStatus?.ExpiryDate && new Date(rider.insuranceStatus.ExpiryDate) > new Date()
                  ? 'bg-green-50 border-green-300' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Shield className={`w-4 h-4 ${
                    rider.insuranceStatus?.IsInsured && rider.insuranceStatus?.ExpiryDate && new Date(rider.insuranceStatus.ExpiryDate) > new Date()
                      ? 'text-green-600' 
                      : 'text-gray-400'
                  }`} />
                  <span className={`text-xs font-bold ${
                    rider.insuranceStatus?.IsInsured && rider.insuranceStatus?.ExpiryDate && new Date(rider.insuranceStatus.ExpiryDate) > new Date()
                      ? 'text-gray-800' 
                      : 'text-gray-500'
                  }`}>{t('common.insurance')}</span>
                </div>
                {rider.insuranceStatus?.IsInsured && rider.insuranceStatus?.ExpiryDate && new Date(rider.insuranceStatus.ExpiryDate) > new Date() ? (
                  <>
                    <div className="flex items-center gap-1 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-semibold text-green-700">Active</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Until {formatDate(rider.insuranceStatus.ExpiryDate)}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">Not insured</p>
                )}
              </div>

              {/* Forfait */}
              <div className={`rounded-lg p-3 border-2 ${
                rider.forfaitStatus?.HasActiveForfait && rider.forfaitStatus?.Forfait
                  ? 'bg-green-50 border-green-300' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className={`w-4 h-4 ${
                    rider.forfaitStatus?.HasActiveForfait && rider.forfaitStatus?.Forfait
                      ? 'text-green-600' 
                      : 'text-gray-400'
                  }`} />
                  <span className={`text-xs font-bold ${
                    rider.forfaitStatus?.HasActiveForfait && rider.forfaitStatus?.Forfait
                      ? 'text-gray-800' 
                      : 'text-gray-500'
                  }`}>{t('common.forfait')}</span>
                </div>
                {rider.forfaitStatus?.HasActiveForfait && rider.forfaitStatus?.Forfait ? (
                  <>
                    <div className="flex items-center gap-1 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-semibold text-green-700">Active</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      {new Date(2000, (rider.forfaitStatus.Forfait.month || rider.forfaitStatus.Forfait.Month) - 1).toLocaleDateString('en-US', { month: 'long' })} {rider.forfaitStatus.Forfait.year || rider.forfaitStatus.Forfait.Year}
                    </p>
                    {(rider.forfaitStatus.Forfait.totalLessons || rider.forfaitStatus.Forfait.TotalLessons) && (
                      <p className="text-xs text-gray-500">
                        {(rider.forfaitStatus.Forfait.totalLessons || rider.forfaitStatus.Forfait.TotalLessons) - (rider.forfaitStatus.Forfait.usedLessons || rider.forfaitStatus.Forfait.UsedLessons || 0)}/{rider.forfaitStatus.Forfait.totalLessons || rider.forfaitStatus.Forfait.TotalLessons} lessons
                      </p>
                    )}
                  </>
                ) : (
                  <div>
                    <p className="text-xs text-gray-500">Inactive</p>
                    {rider.bonusLessonsSummary?.Count > 0 && (
                      <p className="text-xs text-purple-600 font-medium mt-1">
                        {rider.bonusLessonsSummary.Count} bonus
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Extra Lessons and Bonus Cards */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Debt */}
              <div className={`rounded-lg p-3 border ${rider.debtBalance > 0 ? 'bg-rose-50 border-rose-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <CircleDollarSign className={`w-4 h-4 ${rider.debtBalance > 0 ? 'text-rose-600' : 'text-gray-400'}`} />
                  <span className={`text-xs font-bold ${rider.debtBalance > 0 ? 'text-rose-800' : 'text-gray-500'}`}>{t('common.debt')}</span>
                </div>
                {rider.debtBalance > 0 ? (
                  <p className="text-xs font-bold text-rose-600">{formatCurrency(rider.debtBalance)}</p>
                ) : (
                  <p className="text-xs text-gray-500">{t('common.noDebt')}</p>
                )}
              </div>

              {rider.extraLessonsSummary?.Count > 0 && (
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center gap-2 mb-1">
                    <BookOpen className="w-4 h-4 text-orange-600" />
                    <span className="text-xs font-medium text-orange-800">{t('common.extra')}</span>
                  </div>
                  <p className="text-xs font-semibold text-gray-900">
                    {rider.extraLessonsSummary.Count} {t('common.lessons')}
                  </p>
                  {rider.extraLessonsSummary.TotalAmount > 0 && (
                    <p className="text-xs text-gray-600">
                      {formatCurrency(rider.extraLessonsSummary.TotalAmount)}
                    </p>
                  )}
                </div>
              )}
              
              <div className={`rounded-lg p-3 border ${rider.bonusLessonsSummary?.Count > 0 ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Gift className={`w-4 h-4 ${rider.bonusLessonsSummary?.Count > 0 ? 'text-purple-600' : 'text-gray-400'}`} />
                  <span className={`text-xs font-medium ${rider.bonusLessonsSummary?.Count > 0 ? 'text-purple-800' : 'text-gray-500'}`}>{t('common.bonus')}</span>
                </div>
                <p className={`text-xs font-semibold ${rider.bonusLessonsSummary?.Count > 0 ? 'text-gray-900' : 'text-gray-500'}`}>
                  {rider.bonusLessonsSummary?.Count ?? 0} {t('common.lessons')}
                </p>
                {rider.bonusLessonsSummary?.TotalAmount > 0 && (
                  <p className="text-xs text-gray-600">
                    {formatCurrency(rider.bonusLessonsSummary.TotalAmount)}
                  </p>
                )}
              </div>
            </div>
            
            {/* Payment Progress */}
            <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-medium text-blue-800">Total Paid (This Year)</span>
                <span className="text-xs font-bold text-blue-700">{formatCurrency(rider.totalPaidThisYear || 0)}</span>
              </div>
              {rider.forfaitStatus?.HasActiveForfait && rider.forfaitStatus?.Forfait && (
                <div className="flex items-center gap-1">
                  <div className="flex-1 h-1.5 bg-blue-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-600 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(100, ((rider.totalPaidThisYear || 0) / (rider.forfaitStatus.Forfait.amount || rider.forfaitStatus.Forfait.Amount || 1)) * 100)}%` 
                      }}
                    />
                  </div>
                  <span className="text-[9px] text-blue-600 font-medium">
                    {Math.min(100, Math.round(((rider.totalPaidThisYear || 0) / (rider.forfaitStatus.Forfait.amount || rider.forfaitStatus.Forfait.Amount || 1)) * 100))}%
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  onClick={() => openPaymentModal(rider)}
                  className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t('common.addPayment')}
                </button>
                <button
                  onClick={() => openPaymentHistory(rider)}
                  className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  {t('common.history')}
                </button>
              </div>
              {isSuperAdmin && (
                <>
                  <button
                    onClick={() => {
                      setSelectedRider(rider);
                      setHistoricalPaymentForm(prev => ({
                        ...prev,
                        amount: String(insurancePrice),
                        type: 1,
                        description: 'Annual Insurance'
                      }));
                      setShowHistoricalPaymentModal(true);
                    }}
                    className="w-full bg-yellow-100 text-yellow-800 px-3 py-2 rounded-lg text-sm font-medium hover:bg-yellow-200 flex items-center justify-center gap-2 border border-yellow-300"
                  >
                    <Receipt className="w-4 h-4" />
                    Add Historical Payment
                  </button>
                  <button
                    onClick={() => {
                      setBonusRider(rider);
                      setBonusLessonCount(1);
                      setShowBonusModal(true);
                    }}
                    className="w-full bg-purple-100 text-purple-800 px-3 py-2 rounded-lg text-sm font-medium hover:bg-purple-200 flex items-center justify-center gap-2 border border-purple-300"
                  >
                    <Gift className="w-4 h-4" />
                    Give Bonus
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
          </div>
      </div>

        {/* Pagination - Sticky at bottom */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-700 font-medium">
              Showing <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="font-bold text-blue-600">{totalItems}</span> results
            </div>
            
            <div className="flex items-center space-x-2">
                <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
                >
{t('common.previous')}
                </button>
          
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
          
                <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] touch-manipulation"
          >
{t('common.next')}
                </button>
            </div>
          </div>
        </div>
        </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedRider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[98vh] overflow-hidden flex flex-col mx-1" style={{ overflowX: 'hidden' }}>
            {/* Header */}
            <div className="p-3 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-base font-semibold text-gray-900">
{t('common.addPayment')} - {selectedRider.firstName} {selectedRider.lastName}
              </h2>
            </div>
            
            {/* Split Layout: Recap Left, Form Right */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
              {/* Left Side - Recap Section */}
              <div className="w-1/2 border-r border-gray-200 overflow-y-auto overflow-x-hidden p-3">
              
              {/* Payment Status Summary - Always visible - Make it more prominent */}
                <div className="mb-3 p-3 bg-gradient-to-br from-blue-50 via-indigo-50 to-white border border-blue-200 rounded-lg shadow-sm w-full" style={{ minWidth: 0 }}>
                  <div className="flex items-center justify-between mb-2 w-full">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                      Payment Status Recap
                  </p>
                  </div>
                  {isLoadingRiderStatus && (
                      <div className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded-md border border-blue-200">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                        <span className="font-medium">Loading...</span>
                    </div>
                  )}
                </div>
                
                {isLoadingRiderStatus ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-blue-700 py-4 bg-white rounded-lg border border-blue-200 px-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="font-medium">Loading fresh data from backend...</span>
                  </div>
                ) : (
                    <div className="space-y-3 w-full">
                    {/* Annual Insurance Status - Always show */}
                      <div className="bg-gradient-to-r from-blue-50 to-white p-3 rounded-lg border border-blue-200 shadow-sm w-full" style={{ minWidth: 0 }}>
                        <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
                          <Shield className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="text-gray-800 font-semibold text-xs whitespace-nowrap">Annual Insurance</span>
                        </div>
                        {(() => {
                          // Check backend data from state - handle both camelCase and PascalCase
                          // Backend returns camelCase: expiryDate, isCancelled
                          if (!riderInsurance) {
                            return (
                              <div className="text-right flex flex-col items-end">
                                <span className="text-red-700 font-semibold text-sm flex items-center gap-1.5">
                                  <XCircle className="w-4 h-4" />
                                  Not paid
                                </span>
                                <div className="text-xs text-red-600 mt-1">
                                  Required for all payments
                                </div>
                              </div>
                            );
                          }
                          
                          const expiryDate = riderInsurance.expiryDate || riderInsurance.ExpiryDate;
                          
                          if (expiryDate) {
                            const expiry = new Date(expiryDate);
                            const now = new Date();
                            const isCancelled = riderInsurance.isCancelled || riderInsurance.IsCancelled || false;
                            const isValid = !isCancelled && expiry > now;
                            const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
                            
                            return (
                              <div className="text-right flex flex-col items-end space-y-0.5 flex-shrink-0">
                                <div className={`font-semibold text-xs flex items-center gap-1 flex-wrap ${isValid ? 'text-green-700' : 'text-red-700'}`}>
                                  {isValid ? (
                                    <>
                                      <span className="text-green-600">✓</span>
                                      <span>Active until</span>
                                      <span className="font-bold">{expiry.toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        year: 'numeric' 
                                      })}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-red-600">✗</span>
                                      <span>{isCancelled ? 'Cancelled' : 'Expired'}:</span>
                                      <span>{expiry.toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        year: 'numeric' 
                                      })}</span>
                                    </>
                                  )}
                                </div>
                                {isValid && daysUntilExpiry <= 30 && daysUntilExpiry > 0 && (
                                  <div className="text-[10px] text-orange-700 bg-orange-50 px-2 py-0.5 rounded border border-orange-200 font-medium">
                                    ⚠ Expires in {daysUntilExpiry} {daysUntilExpiry === 1 ? 'day' : 'days'}
                                  </div>
                                )}
                                {!isValid && (
                                  <div className="text-[10px] text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200 font-medium">
                                    ⚠ Must renew before other payments
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return (
                            <div className="text-right flex flex-col items-end flex-shrink-0">
                              <span className="text-red-700 font-semibold text-xs flex items-center gap-1 whitespace-nowrap">
                                <XCircle className="w-3.5 h-3.5" />
                              Not paid
                            </span>
                              <div className="text-[10px] text-red-600 mt-0.5">
                                Required for all payments
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    
                    {/* Current Month Forfait Status - Always show */}
                      <div className="bg-gradient-to-r from-green-50 to-white p-3 rounded-lg border border-green-200 shadow-sm w-full" style={{ minWidth: 0 }}>
                        <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="p-1.5 bg-green-100 rounded-lg flex-shrink-0">
                            <Calendar className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-gray-800 font-semibold text-xs whitespace-nowrap">Current Month Forfait</span>
                        </div>
                        {(() => {
                          const currentDate = new Date();
                          const currentYear = currentDate.getFullYear();
                          const currentMonth = currentDate.getMonth() + 1;
                          
                          // Handle both camelCase and PascalCase from backend
                          const forfaitYear = riderActiveForfait?.year || riderActiveForfait?.Year;
                          const forfaitMonth = riderActiveForfait?.month || riderActiveForfait?.Month;
                          const isCancelled = riderActiveForfait?.isCancelled || riderActiveForfait?.IsCancelled;
                          
                          if (riderActiveForfait && 
                              !isCancelled &&
                              forfaitYear === currentYear &&
                              forfaitMonth === currentMonth) {
                            const totalLessons = riderActiveForfait.totalLessons || riderActiveForfait.TotalLessons || 0;
                            const usedLessons = riderActiveForfait.usedLessons || riderActiveForfait.UsedLessons || 0;
                            const remainingLessons = totalLessons - usedLessons;
                            const expiryDate = (riderActiveForfait.expiryDate || riderActiveForfait.ExpiryDate) 
                              ? new Date(riderActiveForfait.expiryDate || riderActiveForfait.ExpiryDate) 
                              : null;
                            const isExpired = expiryDate && expiryDate <= currentDate;
                            
                            return (
                              <div className="text-right flex flex-col items-end space-y-0.5 flex-shrink-0">
                                <div className={`font-semibold text-xs flex items-center gap-1 flex-wrap ${isExpired ? 'text-red-700' : 'text-green-700'}`}>
                                  {isExpired ? (
                                    <>
                                      <span className="text-red-600">✗</span>
                                      <span>Expired:</span>
                                      <span>{expiryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-green-600">✓</span>
                                      <span>Active</span>
                                      <span className="font-bold">{remainingLessons}/{totalLessons}</span>
                                      <span className="text-gray-600 text-[10px]">lessons</span>
                                    </>
                                  )}
                                </div>
                                {expiryDate && !isExpired && (
                                  <div className="text-[10px] text-gray-600">
                                    Expires: {expiryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </div>
                                )}
                                {isExpired && (
                                  <div className="text-[10px] text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200 font-medium">
                                    ⚠ Renew required
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          // Check if there's a forfait but not for current month - handle camelCase
                          const hasFutureForfait = riderForfaits && riderForfaits.some(f => {
                            const cancelled = f.isCancelled || f.IsCancelled;
                            const year = f.year || f.Year;
                            const month = f.month || f.Month;
                            return !cancelled && 
                              (year > currentYear || (year === currentYear && month > currentMonth));
                          });
                          
                          return (
                            <div className="text-right flex flex-col items-end">
                              <span className="text-red-700 font-semibold text-sm flex items-center gap-1.5">
                              <XCircle className="w-4 h-4" />
                              Not paid
                            </span>
                              {hasFutureForfait && (
                                <div className="text-xs text-blue-600 mt-1 font-medium">
                                  Future month already paid
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    
                    {/* Last Paid Forfait Month */}
                      {lastPaidForfait ? (
                        <div className="bg-gradient-to-r from-blue-50 to-white p-3 rounded-lg border border-blue-200 shadow-sm w-full" style={{ minWidth: 0 }}>
                          <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="p-1.5 bg-blue-100 rounded-lg flex-shrink-0">
                              <Receipt className="w-4 h-4 text-blue-600" />
                          </div>
                            <span className="text-gray-800 font-semibold text-xs whitespace-nowrap">Last Paid Forfait</span>
                          </div>
                          <div className="text-right flex flex-col items-end space-y-0.5 flex-shrink-0">
                            <span className="text-blue-700 font-semibold text-xs whitespace-nowrap">
                              {new Date(
                                lastPaidForfait.year || lastPaidForfait.Year, 
                                (lastPaidForfait.month || lastPaidForfait.Month) - 1, 
                                1
                              ).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </span>
                            {(lastPaidForfait.totalLessons !== undefined || lastPaidForfait.TotalLessons !== undefined) && (
                              <div className="text-[10px] text-gray-600">
                                {(lastPaidForfait.usedLessons || lastPaidForfait.UsedLessons || 0)}/{(lastPaidForfait.totalLessons || lastPaidForfait.TotalLessons)} lessons used
                      </div>
                    )}
                      </div>
                        </div>
                      </div>
                      ) : null}
                      
                      {/* Recommendations - What can be purchased */}
                      <div className="mt-2 p-3 bg-gradient-to-br from-green-50 via-blue-50 to-indigo-50 rounded-lg border border-green-200 shadow-sm w-full" style={{ minWidth: 0 }}>
                        <p className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-2">
                          <span className="text-base">💡</span>
                          What can be purchased
                        </p>
                        <div className="space-y-2">
                        {(() => {
                          const hasValidInsurance = riderInsurance && 
                            !(riderInsurance.isCancelled || riderInsurance.IsCancelled) && 
                            (riderInsurance.expiryDate || riderInsurance.ExpiryDate) &&
                            new Date(riderInsurance.expiryDate || riderInsurance.ExpiryDate) > new Date();
                          
                          const currentDate = new Date();
                          const hasActiveForfait = riderActiveForfait && 
                            !(riderActiveForfait.isCancelled || riderActiveForfait.IsCancelled) &&
                            (riderActiveForfait.year || riderActiveForfait.Year) === currentDate.getFullYear() &&
                            (riderActiveForfait.month || riderActiveForfait.Month) === currentDate.getMonth() + 1;
                          
                          const recommendations = [];
                          
                          if (!hasValidInsurance) {
                            recommendations.push({
                              text: 'Annual Insurance (Required first)',
                              color: 'text-red-700',
                              bgColor: 'bg-red-50 border-red-200',
                              icon: '⚠️'
                            });
                          }
                          
                          if (hasValidInsurance && !hasActiveForfait) {
                            recommendations.push({
                              text: `Full Forfait (${forfaitPrice.toLocaleString()} DA) or Demi Forfait (${demiForfaitPrice.toLocaleString()} DA)`,
                              color: 'text-green-700',
                              bgColor: 'bg-green-50 border-green-200',
                              icon: '✓'
                            });
                          }
                          
                          if (hasValidInsurance) {
                            recommendations.push({
                              text: `Pay Per Session (${payPerSessionPrice.toLocaleString()} DA) or Extra Lessons`,
                              color: 'text-blue-700',
                              bgColor: 'bg-blue-50 border-blue-200',
                              icon: '✓'
                            });
                          }
                          
                          if (recommendations.length === 0) {
                            recommendations.push({
                              text: 'All payments up to date. Can purchase extra lessons if needed.',
                              color: 'text-green-700',
                              bgColor: 'bg-green-50 border-green-200',
                              icon: '✅'
                            });
                          }
                          
                          return recommendations.map((rec, idx) => (
                            <div key={idx} className={`${rec.bgColor} ${rec.color} px-2.5 py-1.5 rounded-md border flex items-center gap-2 font-medium text-xs`}>
                              <span className="text-sm">{rec.icon}</span>
                              <span>{rec.text}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                    
                    {/* Insurance Expiry Warning */}
                      {riderInsurance && !(riderInsurance.isCancelled || riderInsurance.IsCancelled) && (riderInsurance.expiryDate || riderInsurance.ExpiryDate) && new Date(riderInsurance.expiryDate || riderInsurance.ExpiryDate) <= new Date() && (
                        <div className="mt-2 p-3 bg-red-100 border-2 border-red-400 rounded-lg shadow-md">
                        <div className="flex items-start gap-2">
                          <span className="text-red-700 font-bold text-lg">⚠</span>
                          <div>
                            <span className="text-red-900 font-extrabold block mb-1 text-sm">Annual Insurance Expired!</span>
                            <span className="text-red-800 text-xs font-semibold">
                              Rider must renew annual insurance before purchasing monthly forfaits or extra lessons.
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
              {/* Right Side - Form Section */}
              <div className="w-1/2 overflow-y-auto overflow-x-hidden p-2">
                <form id="rider-payment-form" onSubmit={handleCreatePayment} className="space-y-2 w-full" style={{ minWidth: 0 }}>
                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-0.5">
{t('common.paymentType')}
                  </label>
                  <select
                    value={paymentForm.type}
                    onChange={(e) => handlePaymentTypeChange(e.target.value)}
                    className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    {paymentTypes.map(type => (
                      <option 
                        key={type.value} 
                        value={type.value}
                        disabled={!type.enabled}
                        className={!type.enabled ? 'text-gray-400' : ''}
                      >
                        {type.label} {!type.enabled ? '(Not Available)' : ''}
                      </option>
                    ))}
                  </select>
                  
                  {/* Payment Types Info - Compact */}
                  <div className="mt-0.5 space-y-0.5">
                    {paymentTypes.map(type => (
                      <div key={type.value} className="flex items-center justify-between text-[10px]">
                        <div className="flex items-center gap-1">
                          <div className={`w-1 h-1 rounded-full ${type.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className={type.enabled ? 'text-gray-700' : 'text-gray-400'}>
                          {type.label}: {type.defaultAmount.toLocaleString()} DA
                        </span>
                        </div>
                        {type.status === 'paid' && (
                          <span className="text-green-600 text-[10px]">✓</span>
                        )}
                      </div>
                    ))}
                </div>

                  {/* Warning if rider doesn't have valid insurance */}
                  {!riderInsurance || !(!riderInsurance.IsCancelled && riderInsurance.ExpiryDate && new Date(riderInsurance.ExpiryDate) > new Date()) ? (
                    <div className="mt-1 bg-yellow-50 border border-yellow-200 rounded p-1.5">
                      <div className="flex gap-1.5">
                        <svg className="h-3 w-3 text-yellow-400 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-[10px] text-yellow-800 leading-tight">
                            <strong>Important:</strong> This rider must have valid annual insurance before purchasing monthly forfaits or extra lessons.
                        </p>
                    </div>
                  </div>
                  ) : null}
                </div>


                {/* Month Selection - Only show for Full Forfait and Demi Forfait */}
                {(paymentForm.type === 2 || paymentForm.type === 4) && (
                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-0.5">
                      Select Months for Forfait
                    </label>
                    
                    {/* Display existing forfaits - Compact */}
                    {riderForfaits.length > 0 && (
                      <div className="mb-1 p-1 bg-blue-50 border border-blue-200 rounded text-[10px]">
                        <p className="font-semibold text-blue-900 mb-0.5">Existing:</p>
                        <div className="flex flex-wrap gap-0.5">
                          {riderForfaits.slice(0, 3).map((forfait, idx) => {
                            const forfaitDate = new Date((forfait.year || forfait.Year), (forfait.month || forfait.Month) - 1);
                            const monthName = forfaitDate.toLocaleDateString('en-US', { month: 'short' });
                            const isCancelled = forfait.isCancelled || forfait.IsCancelled;
                            return (
                              <span
                                key={idx}
                                className={`px-1 py-0.5 rounded text-[9px] ${
                                  isCancelled
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-green-100 text-green-800'
                                }`}
                              >
                                {monthName}
                              </span>
                            );
                          })}
                          {riderForfaits.length > 3 && <span className="text-gray-600">+{riderForfaits.length - 3}</span>}
                        </div>
                      </div>
                    )}
                    
                    <div className="border border-gray-300 rounded p-0.5 max-h-24 overflow-y-auto">
                      <div className="space-y-0.5">
                        {/* Current Year */}
                        <div>
                          <h4 className="font-medium text-gray-800 mb-0.5 text-[10px]">{new Date().getFullYear()}</h4>
                          <div className="grid grid-cols-4 gap-0.5">
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
                              const isSelected = isMonthSelected(new Date().getFullYear(), month);
                              const isPastMonth = month < new Date().getMonth() + 1;
                              const isCurrentMonth = month === new Date().getMonth() + 1;
                              // Check if this month already has a forfait
                              const hasExistingForfait = riderForfaits.some(f => 
                                f.year === new Date().getFullYear() && 
                                f.month === month && 
                                !f.isCancelled
                              );
                              
                              return (
                                <button
                                  key={month}
                                  type="button"
                                  onClick={() => !isPastMonth && !hasExistingForfait && toggleMonthSelection(new Date().getFullYear(), month)}
                                  disabled={isPastMonth || hasExistingForfait}
                                  title={hasExistingForfait ? 'This month already has a forfait' : ''}
                                  className={`p-0.5 text-[9px] rounded border transition-all ${
                                    hasExistingForfait
                                      ? 'bg-purple-100 border-purple-400 text-purple-700 cursor-not-allowed'
                                      : isSelected 
                                      ? 'bg-green-100 border-green-500 text-green-800' 
                                      : isPastMonth
                                      ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                      : isCurrentMonth
                                      ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                                  }`}
                                >
                                  {new Date(2024, month - 1).toLocaleDateString('en-US', { month: 'short' })}
                                  {hasExistingForfait && ' ✓'}
                                  {isCurrentMonth && !hasExistingForfait && '*'}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        
                        {/* Next Year */}
                        <div>
                          <h4 className="font-medium text-gray-800 mb-0.5 text-[10px]">{new Date().getFullYear() + 1}</h4>
                          <div className="grid grid-cols-4 gap-0.5">
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
                              const isSelected = isMonthSelected(new Date().getFullYear() + 1, month);
                              // Check if this month already has a forfait
                              const hasExistingForfait = riderForfaits.some(f => 
                                f.year === new Date().getFullYear() + 1 && 
                                f.month === month && 
                                !f.isCancelled
                              );
                              
                              return (
                                <button
                                  key={month}
                                  type="button"
                                  onClick={() => !hasExistingForfait && toggleMonthSelection(new Date().getFullYear() + 1, month)}
                                  disabled={hasExistingForfait}
                                  title={hasExistingForfait ? 'This month already has a forfait' : ''}
                                  className={`p-0.5 text-[9px] rounded border transition-all ${
                                    hasExistingForfait
                                      ? 'bg-purple-100 border-purple-400 text-purple-700 cursor-not-allowed'
                                      : isSelected 
                                      ? 'bg-green-100 border-green-500 text-green-800' 
                                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                                  }`}
                                >
                                  {new Date(2024, month - 1).toLocaleDateString('en-US', { month: 'short' })}
                                  {hasExistingForfait && ' ✓'}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {paymentForm.selectedMonths.length > 0 && (
                      <div className="mt-1">
                        <p className="text-[10px] text-gray-600 mb-0.5">Selected:</p>
                        <div className="flex flex-wrap gap-0.5">
                          {paymentForm.selectedMonths.map((month, index) => (
                            <span
                              key={index}
                              className="px-1 py-0.5 bg-green-100 text-green-800 rounded text-[9px] flex items-center gap-0.5"
                            >
                              {new Date(2024, month.month - 1).toLocaleDateString('en-US', { month: 'short' })} {month.year}
                              <button
                                type="button"
                                onClick={() => toggleMonthSelection(month.year, month.month)}
                                className="text-green-600 hover:text-green-800 text-[10px]"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <p className="text-[9px] text-gray-600 mt-1 leading-tight">
                      💡 Select months. Months with existing forfaits are marked with ✓.
                    </p>
                  </div>
                )}

                {/* Extra Lesson Count - Only show for Extra Lessons */}
                {paymentForm.type === 3 && (
                  <>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-0.5">
                      Number of Extra Lessons
                    </label>
                    <select
                      value={paymentForm.extraLessonCount}
                      onChange={(e) => handleExtraLessonCountChange(e.target.value)}
                      className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    >
                      {Array.from({ length: 20 }, (_, i) => i + 1).map(lesson => (
                        <option key={lesson} value={lesson}>
                          {lesson} lesson{lesson > 1 ? 's' : ''}
                        </option>
                      ))}
                    </select>
                    </div>
                    
                    {/* Month Selection for Extra Lessons */}
                    <div>
                      <label className="block text-[10px] font-medium text-gray-700 mb-0.5">
                        Select Month (Optional)
                      </label>
                      {lastPaidForfait && (
                        <div className="mb-1 p-1 bg-blue-50 border border-blue-200 rounded text-[9px]">
                          <span className="text-blue-800">
                            Up to {new Date(2000, (lastPaidForfait.month || lastPaidForfait.Month) - 1).toLocaleDateString('en-US', { month: 'short' })} {(lastPaidForfait.year || lastPaidForfait.Year)}
                          </span>
                  </div>
                )}
                      <div className="border border-gray-300 rounded p-0.5 max-h-20 overflow-y-auto">
                        <div className="space-y-0.5">
                          {/* Current Year */}
                          <div>
                            <h4 className="font-medium text-gray-800 mb-0.5 text-[10px]">{new Date().getFullYear()}</h4>
                            <div className="grid grid-cols-4 gap-0.5">
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
                                const isSelected = paymentForm.selectedMonths.some(m => m.year === new Date().getFullYear() && m.month === month);
                                const isPastMonth = month < new Date().getMonth() + 1;
                                const isCurrentMonth = month === new Date().getMonth() + 1;
                                const currentYear = new Date().getFullYear();
                                
                                // Check if this month has a forfait - handle both camelCase and PascalCase
                                const hasForfait = riderForfaits.some(f => {
                                  const fYear = f.year || f.Year;
                                  const fMonth = f.month || f.Month;
                                  const fCancelled = f.isCancelled || f.IsCancelled;
                                  return fYear === currentYear && 
                                         fMonth === month && 
                                         !fCancelled;
                                });
                                
                                // Check if this month is beyond the last paid forfait
                                const lastPaidYear = lastPaidForfait?.year || lastPaidForfait?.Year;
                                const lastPaidMonth = lastPaidForfait?.month || lastPaidForfait?.Month;
                                const isBeyondLastForfait = lastPaidForfait && 
                                  (currentYear > lastPaidYear || 
                                   (currentYear === lastPaidYear && month > lastPaidMonth));
                                
                                // Month is valid if: NOT has forfait AND not past AND not beyond last paid forfait
                                // (We want to select months that DON'T have forfaits yet)
                                const isValidMonth = !hasForfait && !isPastMonth && !isBeyondLastForfait;
                                
                                return (
                                  <button
                                    key={month}
                                    type="button"
                                    onClick={() => {
                                      if (isValidMonth) {
                                        // Toggle selection for valid months
                                        if (isSelected) {
                                          setPaymentForm(prev => ({
                                            ...prev,
                                            selectedMonths: prev.selectedMonths.filter(m => !(m.year === currentYear && m.month === month))
                                          }));
                                        } else {
                                          // Replace any existing selection with this one
                                          setPaymentForm(prev => ({
                                            ...prev,
                                            selectedMonths: [{ year: currentYear, month }]
                                          }));
                                        }
                                      }
                                    }}
                                    disabled={!isValidMonth}
                                    title={
                                      isPastMonth 
                                        ? 'Cannot select past months' 
                                        : hasForfait 
                                        ? 'This month already has a forfait' 
                                        : isBeyondLastForfait 
                                        ? `Cannot select months after ${new Date(2000, (lastPaidForfait?.month || lastPaidForfait?.Month || 1) - 1).toLocaleDateString('en-US', { month: 'long' })} ${lastPaidForfait?.year || lastPaidForfait?.Year}. Purchase a forfait first.`
                                        : 'Click to select this month'
                                    }
                                    className={`p-0.5 text-[9px] rounded border transition-all ${
                                      isSelected
                                        ? 'bg-orange-100 border-orange-500 text-orange-800'
                                        : !isValidMonth
                                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                        : isCurrentMonth
                                        ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                                        : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    {new Date(2024, month - 1).toLocaleDateString('en-US', { month: 'short' })}
                                    {hasForfait && !isPastMonth && !isBeyondLastForfait && ' ✓'}
                                    {isCurrentMonth && '*'}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          
                          {/* Next Year */}
                          <div>
                            <h4 className="font-medium text-gray-800 mb-0.5 text-[10px]">{new Date().getFullYear() + 1}</h4>
                            <div className="grid grid-cols-4 gap-0.5">
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
                                const isSelected = paymentForm.selectedMonths.some(m => m.year === new Date().getFullYear() + 1 && m.month === month);
                                const nextYear = new Date().getFullYear() + 1;
                                
                                // Check if this month has a forfait - handle both camelCase and PascalCase
                                const hasForfait = riderForfaits.some(f => {
                                  const fYear = f.year || f.Year;
                                  const fMonth = f.month || f.Month;
                                  const fCancelled = f.isCancelled || f.IsCancelled;
                                  return fYear === nextYear && 
                                         fMonth === month && 
                                         !fCancelled;
                                });
                                
                                // Check if this month is beyond the last paid forfait
                                const lastPaidYear = lastPaidForfait?.year || lastPaidForfait?.Year;
                                const lastPaidMonth = lastPaidForfait?.month || lastPaidForfait?.Month;
                                const isBeyondLastForfait = lastPaidForfait && 
                                  (nextYear > lastPaidYear || 
                                   (nextYear === lastPaidYear && month > lastPaidMonth));
                                
                                // Month is valid if: NOT has forfait AND not beyond last paid forfait
                                // (We want to select months that DON'T have forfaits yet)
                                const isValidMonth = !hasForfait && !isBeyondLastForfait;
                                
                                return (
                                  <button
                                    key={month}
                                    type="button"
                                    onClick={() => {
                                      if (isValidMonth) {
                                        // Toggle selection
                                        if (isSelected) {
                                          setPaymentForm(prev => ({
                                            ...prev,
                                            selectedMonths: prev.selectedMonths.filter(m => !(m.year === nextYear && m.month === month))
                                          }));
                                        } else {
                                          // Replace any existing selection with this one
                                          setPaymentForm(prev => ({
                                            ...prev,
                                            selectedMonths: [{ year: nextYear, month }]
                                          }));
                                        }
                                      }
                                    }}
                                    disabled={!isValidMonth}
                                    title={
                                      isBeyondLastForfait 
                                        ? `Cannot select months after ${new Date(2000, (lastPaidForfait?.month || lastPaidForfait?.Month || 1) - 1).toLocaleDateString('en-US', { month: 'long' })} ${lastPaidForfait?.year || lastPaidForfait?.Year}. Purchase a forfait first.`
                                        : hasForfait 
                                        ? 'This month already has a forfait' 
                                        : 'Click to select this month'
                                    }
                                    className={`p-0.5 text-[9px] rounded border transition-all ${
                                      isSelected
                                        ? 'bg-orange-100 border-orange-500 text-orange-800'
                                        : !isValidMonth
                                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                                    }`}
                                  >
                                    {new Date(2024, month - 1).toLocaleDateString('en-US', { month: 'short' })}
                                    {hasForfait && !isBeyondLastForfait && ' ✓'}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {paymentForm.selectedMonths.length > 0 && (
                        <div className="mt-1">
                          <p className="text-[10px] text-gray-600 mb-0.5">Selected:</p>
                          {paymentForm.selectedMonths.map((month, index) => (
                            <span
                              key={index}
                              className="px-1 py-0.5 bg-orange-100 text-orange-800 rounded text-[9px] inline-block"
                            >
                              {new Date(2024, month.month - 1).toLocaleDateString('en-US', { month: 'short' })} {month.year}
                            </span>
                          ))}
                  </div>
                      )}
                    </div>
                  </>
                )}

                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-0.5">
{t('common.amount')}
                    </label>
                      <input
                        type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                      className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    placeholder={(() => {
                      const paymentType = paymentTypes.find(pt => pt.value === paymentForm.type);
                      return paymentType?.defaultAmount?.toString() || '';
                    })()}
                    required
                  />
                  {(() => {
                    const paymentType = paymentTypes.find(pt => pt.value === paymentForm.type);
                    if (paymentType?.defaultAmount !== undefined) {
                      if ((paymentForm.type === 2 || paymentForm.type === 4) && paymentForm.selectedMonths.length > 1) {
                        return (
                            <p className="text-[9px] text-blue-600 mt-0.5">
                              💡 {paymentType.defaultAmount.toLocaleString()} × {paymentForm.selectedMonths.length} = {paymentForm.amount} DA
                          </p>
                        );
                      } else if (paymentForm.type === 3 && paymentForm.extraLessonCount > 1) {
                        return (
                            <p className="text-[9px] text-orange-600 mt-0.5">
                              💡 {paymentType.defaultAmount.toLocaleString()} × {paymentForm.extraLessonCount} = {paymentForm.amount} DA
                          </p>
                        );
                      } else {
                        const colorClass = paymentForm.type === 1 ? 'text-blue-600' 
                          : paymentForm.type === 2 || paymentForm.type === 4 ? 'text-green-600' 
                          : paymentForm.type === 5 ? 'text-purple-600'
                          : 'text-orange-600';
                        return (
                          <p className={`text-[9px] mt-0.5 ${colorClass}`}>
                            💡 {t('common.suggested')}: {paymentType.defaultAmount.toLocaleString()} DA
                          </p>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>

                <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-0.5">
{t('common.description')}
                  </label>
                  <input
                    type="text"
                    value={paymentForm.description}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  </div>

                {/* Activate on Credit (Debt) Toggle - only for forfait types */}
                {(paymentForm.type === 2 || paymentForm.type === 4 || paymentForm.type === 5) && (
                  <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <label className="flex items-center justify-between cursor-pointer gap-2">
                      <div>
                        <p className="text-[11px] font-medium text-gray-800">Activate on Credit (Add to Debt)</p>
                        <p className="text-[9px] text-gray-500">No payment collected now. The member will owe this amount and you can track/settle it later.</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={paymentForm.activateOnCredit}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, activateOnCredit: e.target.checked }))}
                        className="w-4 h-4 accent-amber-500"
                      />
                    </label>
                    {paymentForm.activateOnCredit && (
                      <p className="text-[9px] font-medium text-amber-700 mt-1">
                        {paymentForm.selectedMonths.length} month(s) × {parseFloat(paymentForm.amount || 0).toLocaleString()} DA will be added to {selectedRider?.firstName || selectedRider?.name || 'the member'}'s debt.
                      </p>
                    )}
                  </div>
                )}

                </form>
              </div>
              </div>
              
               {/* Fixed Footer with Buttons */}
               <div className="p-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                 <div className="flex flex-col sm:flex-row gap-1.5">
                  <button
                    type="submit"
                    form="rider-payment-form"
                  className="flex-1 bg-blue-600 text-white py-1.5 px-3 rounded-lg text-xs font-medium hover:bg-blue-700"
                  >
{t('common.createPayment')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-1.5 px-3 rounded-lg text-xs font-medium hover:bg-gray-200"
                  >
{t('common.cancel')}
                  </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showPaymentHistory && selectedRider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
{t('common.paymentHistory')} - {selectedRider.firstName} {selectedRider.lastName}
                </h2>
                <div className="flex items-center gap-3">
                  <select
                    value={selectedPaymentType}
                    onChange={(e) => {
                      setSelectedPaymentType(e.target.value);
                      fetchRiderPayments(selectedRider.id, e.target.value);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">{t('common.allTypes')}</option>
                    {paymentTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowPaymentHistory(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {riderPayments.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">{t('common.noPaymentsFound')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {riderPayments.map((payment) => {
                    const paymentType = paymentTypes.find(pt => pt.value === payment.type);
                    const IconComponent = paymentType?.icon || CreditCard;
                    
                    return (
                      <div key={payment.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${paymentType?.color || 'bg-gray-100'}`}>
                              <IconComponent className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-gray-900">{paymentType?.label || payment.type}</h3>
                                {payment.isHistorical && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                                    Historical
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">{payment.description}</p>
                              <p className="text-xs text-gray-400">
                                {formatDate(payment.paymentDate)} • {payment.processedByUserName || 'System'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
                              <div className="flex items-center gap-1">
                                {payment.isCancelled ? (
                                  <>
                                    <XCircle className="w-4 h-4 text-red-500" />
                                    <span className="text-sm text-red-600">{t('common.cancelled')}</span>
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span className="text-sm text-green-600">{t('common.active')}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            {!payment.isCancelled && (
                              <button
                                onClick={() => handleCancelPayment(payment.id, payment.type)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Cancel Payment"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bonus Lessons Modal */}
      {showBonusModal && bonusRider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[98vh] overflow-hidden flex flex-col">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Give Bonus Lessons</h2>
                <button
                  onClick={() => {
                    setShowBonusModal(false);
                    setBonusRider(null);
                    setBonusLessonCount(1);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <Gift className="w-8 h-8 text-purple-600" />
                  <div>
                    <h3 className="font-medium text-purple-900">
                      {bonusRider.firstName} {bonusRider.lastName}
                    </h3>
                    <p className="text-sm text-purple-700">Give bonus lessons to this rider</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleGiveBonusLessons}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Bonus Lessons
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={bonusLessonCount}
                    onChange={(e) => setBonusLessonCount(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Bonus lessons are free (0 DA each)
                  </p>
                </div>

                <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Gift className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-purple-800">
                      <p className="font-medium mb-1">Important:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Bonus lessons are stored on the rider and don't require a forfait</li>
                        <li>Rider can use bonus lessons even without an active monthly forfait</li>
                        <li>Bonus lessons don't expire</li>
                        <li>Total cost: 0 DA (Free bonus lessons)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBonusModal(false);
                      setBonusRider(null);
                      setBonusLessonCount(1);
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    disabled={givingBonus}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={givingBonus}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {givingBonus ? 'Giving...' : `Give ${bonusLessonCount} Bonus Lesson${bonusLessonCount > 1 ? 's' : ''}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Historical Payment Modal */}
      {showHistoricalPaymentModal && selectedRider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[98vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Add Historical Payment - {selectedRider.firstName} {selectedRider.lastName}
                </h2>
                <button
                  onClick={() => {
                    setShowHistoricalPaymentModal(false);
                    setHistoricalPaymentForm({
                      amount: '',
                      type: 1,
                      description: '',
                      extraLessonCount: 1,
                      selectedMonths: [],
                      paymentDate: new Date().toISOString().split('T')[0]
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-yellow-700 mt-2 bg-yellow-50 p-2 rounded border border-yellow-200">
                ⚠️ Historical payments generally do not affect active status, but recent annual insurance payments (made within the last year) will activate the rider's insurance.
              </p>
            </div>
            
            {/* Form */}
            <div className="p-4 overflow-y-auto flex-1">
              <form onSubmit={handleCreateHistoricalPayment}>
                {/* Payment Date */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Date *
                  </label>
                  <input
                    type="date"
                    value={historicalPaymentForm.paymentDate}
                    onChange={(e) => setHistoricalPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    required
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* Payment Type */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Type *
                  </label>
                  <select
                    value={historicalPaymentForm.type}
                    onChange={(e) => {
                      const newType = parseInt(e.target.value);
                      const paymentType = paymentTypes.find(pt => pt.value === newType);
                      let newAmount = historicalPaymentForm.amount;
                      if (newType === 1) newAmount = String(insurancePrice);
                      else if (newType === 2) newAmount = String(forfaitPrice);
                      else if (newType === 3) newAmount = String(extraLessonPrice * historicalPaymentForm.extraLessonCount);
                      
                      setHistoricalPaymentForm(prev => ({ 
                        ...prev, 
                        type: newType,
                        amount: newAmount,
                        description: paymentType?.label || ''
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    required
                  >
                    {paymentTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (DA) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={historicalPaymentForm.amount}
                    onChange={(e) => setHistoricalPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Extra Lesson Count (if type is Extra Lesson) */}
                {historicalPaymentForm.type === 3 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Lessons
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={historicalPaymentForm.extraLessonCount}
                      onChange={(e) => {
                        const count = parseInt(e.target.value) || 1;
                        setHistoricalPaymentForm(prev => ({ 
                          ...prev, 
                          extraLessonCount: count,
                          amount: String(extraLessonPrice * count)
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>
                )}

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={historicalPaymentForm.description}
                    onChange={(e) => setHistoricalPaymentForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    rows="3"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowHistoricalPaymentModal(false);
                      setHistoricalPaymentForm({
                        amount: '',
                        type: 1,
                        description: '',
                        extraLessonCount: 1,
                        selectedMonths: [],
                        paymentDate: new Date().toISOString().split('T')[0]
                      });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    Add Historical Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RiderPayments;